import { gql, TypedDocumentNode } from '@apollo/client';

import {
  Candidate,
  Match,
  MutationAcceptOfferArgs,
  MutationApplyToJobArgs,
  MutationDeclineOfferArgs,
  MutationRequestCallbackArgs,
  MutationUpdateApplicationMatchStatusArgs,
  MutationUpdateCandidateNotificationsArgs,
  RequestCallResponse,
} from '@pflegia/data-access';

import { CANDIDATE_FULL_FIELDS_FRAGMENT } from './fragments/candidate-full-fields';

export const CHANGE_CANDIDATE_PROFILE_STATE = gql`
  mutation changeCandidateProfileState($state: String!, $feedbackItems: [String!], $pauseDurationMonths: Int) {
    changeCandidateProfileState(state: $state, feedbackItems: $feedbackItems, pauseDurationMonths: $pauseDurationMonths) {
      id
      candidateProfileState
    }
  }
`;

export const LOGOUT = gql`
  mutation logout {
    logout
  }
`;

export const FORGOT_PASSWORD_MUTATION = gql`
  mutation forgotPassword($email: String!) {
    forgotPassword(email: $email) {
      message
      messageType
    }
  }
`;

export const CHANGE_PASSWORD_MUTATION = gql`
  mutation updatePassword($newPassword: String!) {
    changePassword(newPassword: $newPassword) {
      message
    }
  }
`;

export const UPDATE_CANDIDATE_BASED_ON_FILTERS = gql`
  mutation updateCandidate($filters: [CandidateUpdateFilterInput!]) {
    data: updateCandidateBasedOnFilters(filters: $filters) {
      ...CandidateFullFields
    }
  }
  ${CANDIDATE_FULL_FIELDS_FRAGMENT}
`;

export const CREATE_CAREER_COACH_CALL_REQUEST = gql`
  mutation createCareerCoachCallRequest($timeSlots: [String!]!) {
    createCareerCoachCallRequest(timeSlots: $timeSlots) {
      timeSlots
    }
  }
`;

export const APPLY: TypedDocumentNode<{ match: Match }, MutationApplyToJobArgs> = gql`
  mutation ApplyToJob(
    $jobPositionId: UUID
    $applicationSource: String!
    $employerId: UUID
    $slimmedJobCard: Boolean
    $utm: CandidateUtmValueInput
  ) {
    match: applyToJob(
      jobPositionId: $jobPositionId
      applicationSource: $applicationSource
      employerId: $employerId
      slimmedJobCard: $slimmedJobCard
      utm: $utm
    ) {
      candidateId
      connectionType
      id
      isMachineLearningInvite
      offerType
      invitationText
      jobPositionId
      connectionStatus
      applicationSource
      inviteAcceptSource
      updatedAt
    }
  }
`;

export const DECLINE: TypedDocumentNode<{ declineOffer: Match }, MutationDeclineOfferArgs> = gql`
  mutation declineOffer($jobPositionId: String!, $feedbackItems: [String!]) {
    declineOffer(jobPositionId: $jobPositionId, feedbackItems: $feedbackItems) {
      candidateId
      connectionType
      id
      invitationText
      jobPositionId
      connectionStatus
      isMachineLearningInvite
      offerType
      inviteAcceptSource
      updatedAt
    }
  }
`;

export const UPDATE_APPLICATION_MATCH_STATUS: TypedDocumentNode<
  { updateApplicationMatchStatus: Match },
  MutationUpdateApplicationMatchStatusArgs
> = gql`
  mutation updateApplicationMatchStatus($onlineMatchStatus: String, $matchId: UUID!, $jobPositionId: UUID) {
    updateApplicationMatchStatus(onlineMatchStatus: $onlineMatchStatus, matchId: $matchId, jobPositionId: $jobPositionId) {
      id
      jobPositionId
      onlineMatchStatus
      updatedAt
    }
  }
`;

export const ACCEPT: TypedDocumentNode<{ acceptOffer: Match }, MutationAcceptOfferArgs> = gql`
  mutation acceptOffer($jobPositionId: UUID!, $inviteAcceptSource: String) {
    acceptOffer(jobPositionId: $jobPositionId, inviteAcceptSource: $inviteAcceptSource) {
      candidateId
      connectionType
      id
      invitationText
      isMachineLearningInvite
      offerType
      jobPositionId
      connectionStatus
      inviteAcceptSource
      updatedAt
    }
  }
`;

export const MARK_ACTIVITY_READ = gql`
  mutation MarkActivityAsRead($id: String!) {
    markActivityAsRead(id: $id) {
      id
      isRead
    }
  }
`;

export const MARK_ALL_ACTIVITY_READ = gql`
  mutation MarkAllAsRead {
    markAllAsRead
  }
`;

export const UPDATE_CONTACT_DETAILS = gql`
  mutation updateContactDetails($input: UpdateContactDetailsInput!) {
    updateContactDetails(input: $input) {
      id
      message
    }
  }
`;

export const RESEND_CONFIRMATION_SMS = gql`
  mutation ResendConfirmationSms($callbackUrl: String!) {
    resendConfirmationSms(callbackUrl: $callbackUrl) {
      message
    }
  }
`;

export type UpdateCandidateNotificationsData = {
  candidate: Pick<
    Candidate,
    | '__typename'
    | 'emailNotifications'
    | 'id'
    | 'isWhatsAppOptInPromptAllowed'
    | 'phoneConfirmedAt'
    | 'smsNotifications'
    | 'whatsAppNotifications'
    | 'newsletterSubscription'
  >;
};
export const UPDATE_CANDIDATE_NOTIFICATIONS: TypedDocumentNode<UpdateCandidateNotificationsData, MutationUpdateCandidateNotificationsArgs> =
  gql`
    mutation UpdateCandidateNotifications($field: String!, $value: Boolean) {
      candidate: updateCandidateNotifications(field: $field, value: $value) {
        emailNotifications
        id
        isWhatsAppOptInPromptAllowed
        newsletterSubscription
        phoneConfirmedAt
        smsNotifications
        whatsAppNotifications
      }
    }
  `;

export const UPDATE_PASSWORD = gql`
  mutation UpdatePassword($currentPassword: String!, $password: String!) {
    updatePassword(currentPassword: $currentPassword, password: $password) {
      message
      messageType
    }
  }
`;

export const SEND_CONTACT_US = gql`
  mutation SendContactUs($fullName: String!, $email: String!, $phoneNumber: String, $company: String, $message: String!) {
    sendContactUs(fullName: $fullName, email: $email, phoneNumber: $phoneNumber, company: $company, message: $message)
  }
`;

export const REQUEST_CALLBACK: TypedDocumentNode<{ requestCallback: RequestCallResponse }, MutationRequestCallbackArgs> = gql`
  mutation requestCallback($chatThreadId: String!, $jobPositionId: String!) {
    requestCallback(chatThreadId: $chatThreadId, jobPositionId: $jobPositionId) {
      success
      chatItem {
        id
        type
        readAt
        textMessage
        candidateFile {
          id
          name
          nameInBucket
        }
        createdAt
        chatThreadId
      }
    }
  }
`;

export const TRACK_ARTICLE_FILE_DOWNLOAD: TypedDocumentNode<
  { trackArticleFileDownload: { fileUrl: string; slug: string } },
  { fileUrl: string; slug: string }
> = gql`
  mutation trackArticleFileDownload($slug: String!, $fileUrl: String!) {
    trackArticleFileDownload(slug: $slug, fileUrl: $fileUrl) {
      slug
      fileUrl
    }
  }
`;
